import React, { useEffect, useMemo, useState } from 'react';
import { from } from 'rxjs';
import { tap, switchMap } from 'rxjs/operators';
import { hasAccess } from 'cp-client-auth!sofe';
import canopyUrls from 'canopy-urls!sofe';
import { CpDropdown, CpButton, CpIcon } from 'canopy-styleguide!sofe';

import { ensurePageReload } from 'src/common/url-helpers';
import { useNav } from '../../nav.context';
import styles from './nav-profile.styles.css';
import { UserReferralDialogComponent } from './user-referral.component';

export const NavProfile = () => {
  const nav = useNav();

  const [notificationCount, setNotificationCount] = useState(null);
  const [referralDialog, setReferralDialog] = useState(false);

  useEffect(() => {
    const notificationSubscription = from(SystemJS.import('notifications-ui!sofe'))
      .pipe(
        tap(nui => nui.initializeMessageCounts()),
        switchMap(nui => nui.globalUnreadNotifications)
      )
      .subscribe(count => {
        if (count === 0) setNotificationCount();
        else if (count > 99) setNotificationCount('99+');
        else setNotificationCount(count);
      });
    return () => notificationSubscription.unsubscribe();
  }, []);

  const userHasAccess = useMemo(() => hasAccess(nav.user), [nav]);
  const supportPhone = '855-616-7305';

  return (
    nav &&
    nav.user && (
      <>
        <CpDropdown
          position="right-end"
          triggerIsBlock
          className={styles.wrapper}
          contentWidth={240}
          onOpen={() => {
            if (nav.secondaryOpen) {
              nav.setSecondaryOpen(null);
            }
          }}
          renderTrigger={({ toggle }) => (
            <a className={styles.wrapper} onClick={toggle}>
              <div className={styles.profileButton}>
                <div className={styles.notificationWrapper}>
                  {notificationCount ? <div className={styles.notificationCount}>{notificationCount}</div> : null}
                </div>
                {nav.user.initials}
              </div>
            </a>
          )}
          renderContent={() => (
            <>
              <div className={styles.chatWithSupport}>
                <CpButton
                  block
                  id={'canopy-intercom-launcher'}
                  btnType="primary"
                  className={styles.supportChat}
                >
                  Chat with Support
                </CpButton>
                <div className={styles.supportPhone}>or call at {supportPhone}</div>
              </div>
              <div className="cp-select-list cp-select-list__primary-menu">
                <div className="cp-divider" />
                <a
                  className={styles.profileLink}
                  href="https://help.getcanopy.com/"
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  <div className="cp-select-list__content">Search support library</div>
                  <CpIcon name="caret-large-right" className="cp-select-list__icon-right" />
                </a>
                <button onClick={() => setReferralDialog(true)}>
                  <div className="cp-select-list__content">Make a referral</div>
                  <CpIcon name="caret-large-right" className="cp-select-list__icon-right" />
                </button>
                <a href="https://community.canopytax.com/" target="_blank">
                  <div className="cp-select-list__content">Ask our community</div>
                  <CpIcon name="caret-large-right" className="cp-select-list__icon-right" />
                </a>
                <a
                  href="https://community.canopytax.com/categories/feature-requests"
                  target="_blank"
                >
                  <div className="cp-select-list__content">Suggest a feature</div>
                  <CpIcon name="caret-large-right" className="cp-select-list__icon-right" />
                </a>
                <a
                  href="https://help.getcanopy.com/en_US/canopy-updates"
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  <div className="cp-select-list__content">Release notes</div>
                  <CpIcon name="caret-large-right" className="cp-select-list__icon-right" />
                </a>
                <div className="cp-divider" />
                <a href="#/notifications/dashboard">
                  <div className="cp-select-list__content">Notifications</div>
                  {notificationCount && (
                    <div className={styles.notificationCount}>
                      {notificationCount}
                    </div>
                  )}
                </a>
                {userHasAccess(['company_settings_activity_log']) && (
                  <a href="#/activity-log">
                    Activity log
                  </a>
                )}
                <a href={`#/global-settings/user/${nav.user.id}`}>
                  Settings
                </a>
                {userHasAccess(['canopy_admin', 'canopy_users', 'canopy_trials']) && (
                  <a href="#/canopy-admin/users">
                    Canopy Admin
                  </a>
                )}
                <a
                  href={ensurePageReload(
                    `${canopyUrls.getAuthUrl()}/#/logout?redirect_url=${encodeURIComponent(window.location.origin)}`,
                  )}
                >
                  Sign out
                </a>
              </div>
            </>
          )}
        />
        {referralDialog && (
          <UserReferralDialogComponent
            firstName={nav.user.first_name}
            lastName={nav.user.last_name}
            email={nav.user.email}
            directoryId={nav.user.id}
            phoneNumber={nav.user.phone_number}
            onClose={() => setReferralDialog(false)}
          />
        )}
      </>
    )
  );
};
